import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useServices} from '@/services/context'
import {useAppConfig} from '@/hooks/use-app-config'
import type {AppConfig, LocalizedTypeValue} from '@/hooks/app-config-helpers'
import {AttributeEntityType} from '@/types/attribute-entity-type'
import {AttributeOperation} from '@/types/attribute-operation'
import type {AttributeDefinition} from '@/types/attribute-definition'

const TEMPLATE_PLACEHOLDER_RE = /\{([^}]+)\}/g

const TYPE_VALUES_KEY_BY_ENTITY: Record<AttributeEntityType, keyof AppConfig | null> = {
    [AttributeEntityType.PARTY]: 'partyTypes',
    [AttributeEntityType.IDENTITY]: 'identityTypes',
    [AttributeEntityType.RESOURCE]: 'resourceTypes',
    [AttributeEntityType.FUNCTION_GROUP]: 'functionGroupTypes',
    [AttributeEntityType.TARGET_SYSTEM]: 'systemTypes',
    [AttributeEntityType.CONNECTOR]: 'connectorTypes',
    [AttributeEntityType.REPORT]: null,
    [AttributeEntityType.ACCESS_PROFILE]: 'systemTypes',
    [AttributeEntityType.ACCOUNT]: 'systemTypes',
    [AttributeEntityType.ENFORCEMENT_OBJECT]: 'enforcementTypes',
}

export function extractTemplateAttributeNames(template: string | null | undefined): string[] {
    if (!template) return []
    const matches = template.matchAll(TEMPLATE_PLACEHOLDER_RE)
    return Array.from(matches, (m) => m[1].trim()).filter((n) => n.length > 0)
}

export interface UseTypedEntityAttributesArgs {
    entityType: AttributeEntityType
    typeCode: string
}

export interface UseTypedEntityAttributesResult {
    displayNameTemplate: string | null
    templateAttributeNames: string[]
    requiredDefinitions: AttributeDefinition[]
    formDefinitions: AttributeDefinition[]
    isLoading: boolean
}

/**
 * Generic resolver for "thing with attributes + display-name template" create
 * forms — works across every {@link AttributeEntityType} that has a typed
 * sub-table (parties, identities, resources, function groups, target systems).
 *
 * Resolves the displayNameTemplate from the matching type-values list in
 * {@link useAppConfig}, fetches the CREATE-able attribute definitions for the
 * given type, and orders them: template-referenced first (drive the displayName),
 * then required, then optional.
 */
export function useTypedEntityAttributes(
    args: UseTypedEntityAttributesArgs,
): UseTypedEntityAttributesResult {
    const {entityType, typeCode} = args
    const services = useServices()
    const appConfig = useAppConfig()

    const displayNameTemplate = useMemo(() => {
        if (!typeCode) return null
        const typeValuesKey = TYPE_VALUES_KEY_BY_ENTITY[entityType]
        if (!typeValuesKey) return null
        const typeValues = appConfig[typeValuesKey] as LocalizedTypeValue[] | undefined
        const typeValue = typeValues?.find((tv) => tv.code === typeCode)
        const settings = typeValue?.settings as {displayNameTemplate?: string | null} | undefined
        return settings?.displayNameTemplate ?? null
    }, [appConfig, entityType, typeCode])

    const templateAttributeNames = useMemo(
        () => extractTemplateAttributeNames(displayNameTemplate),
        [displayNameTemplate],
    )

    const {data: definitionsData, isLoading} = useQuery({
        queryKey: ['attribute-definitions', entityType, typeCode],
        queryFn: () => services.attributeDefinitions.list(entityType, {typeCode}),
        enabled: typeCode.length > 0,
    })

    const formDefinitions = useMemo<AttributeDefinition[]>(() => {
        const all = (definitionsData ?? []) as AttributeDefinition[]
        const editable = all.filter(
            (d) => d.active && d.operations.includes(AttributeOperation.CREATE),
        )
        const fromTemplate = templateAttributeNames
            .map((n) => editable.find((d) => d.name === n))
            .filter((d): d is AttributeDefinition => d != null)
        const seen = new Set(fromTemplate.map((d) => d.id))
        const required = editable.filter((d) => d.required && !seen.has(d.id))
        required.forEach((d) => seen.add(d.id))
        const optional = editable.filter((d) => !seen.has(d.id))
        return [...fromTemplate, ...required, ...optional]
    }, [definitionsData, templateAttributeNames])

    const requiredDefinitions = useMemo<AttributeDefinition[]>(
        () => formDefinitions.filter(
            (d) => d.required || templateAttributeNames.includes(d.name),
        ),
        [formDefinitions, templateAttributeNames],
    )

    return {
        displayNameTemplate,
        templateAttributeNames,
        requiredDefinitions,
        formDefinitions,
        isLoading,
    }
}
